import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';

interface DadosEmpresa {
  idEmpresa: number;
  idEnderecoEmpresa: number;
  nomeFantasia: string;
  cnpj: string;
  contato: string;
  telefone: string;
  celular: string;
  site: string;
  idPais: number | null;
  idEstado: number | null;
  idCidade: number | null;
  cep: string;
  bairro: string;
  rua: string;
  numero: string;
  complemento: string;
  dataAtualizacao: Date;
}

@Component({
  selector: 'app-dados-empresa',
  templateUrl: './dados-empresa.component.html',
  styleUrls: ['./dados-empresa.component.scss']
})
export class DadosEmpresaComponent implements OnInit {

  formDadosEmpresa: FormGroup;
  dadosEmpresa: DadosEmpresa | null = null;
  editando: boolean = false;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private ajaxService: AjaxService,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.inicializarFormulario();
    this.carregarDadosEmpresa();
  }

  inicializarFormulario(): void {
    this.formDadosEmpresa = this.fb.group({
      nomeFantasia: ['', [Validators.required, Validators.minLength(3)]],
      cnpj: ['', [Validators.required, Validators.pattern(/^\d{14,16}$/)]],
      contato: ['', [Validators.required, Validators.minLength(3)]],
      telefone: ['', [Validators.required, Validators.pattern(/^\(\d{2}\) \d{4,5}-\d{4}$/)]],
      celular: ['', [Validators.required, Validators.pattern(/^\(\d{2}\) \d{5}-\d{4}$/)]],
      site: [''],
      cep: ['', [Validators.required, Validators.pattern(/^\d{5}-\d{3}$/)]],
      bairro: ['', [Validators.required, Validators.minLength(2)]],
      rua: ['', [Validators.required, Validators.minLength(3)]],
      numero: ['', [Validators.required]],
      complemento: ['']
    });
  }

  carregarDadosEmpresa(): void {
    this.loading = true;
    this.util.showLoading();

    const url = API + 'empresa/Clinica/BuscarEdicao';
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();

        const data = response?.data ? this.util.converterParaLowercase(response.data) : null;
        const empresa = data?.empresa ? this.util.converterParaLowercase(data.empresa) : null;
        const endereco = empresa?.enderecoempresacadastrodto
          ? this.util.converterParaLowercase(empresa.enderecoempresacadastrodto)
          : null;

        this.dadosEmpresa = {
          idEmpresa: Number(empresa?.idempresa || 0),
          idEnderecoEmpresa: Number(endereco?.idenderecoempresa || 0),
          nomeFantasia: this.toText(empresa?.nomefantasia),
          cnpj: this.toText(empresa?.cnpj),
          contato: this.toText(empresa?.contato),
          telefone: this.toText(empresa?.registrobombeiros),
          celular: this.toText(empresa?.registroprefeitura),
          site: this.toText(empresa?.registrocro),
          idPais: this.toNumberOrNull(endereco?.idpais),
          idEstado: this.toNumberOrNull(endereco?.idestado),
          idCidade: this.toNumberOrNull(endereco?.idcidade),
          cep: this.toText(endereco?.cep),
          bairro: this.toText(endereco?.bairro),
          rua: this.toText(endereco?.rua),
          numero: this.toText(endereco?.numero),
          complemento: this.toText(endereco?.complemento),
          dataAtualizacao: new Date()
        };

        this.formDadosEmpresa.patchValue({
          nomeFantasia: this.dadosEmpresa.nomeFantasia,
          cnpj: this.dadosEmpresa.cnpj,
          contato: this.dadosEmpresa.contato,
          telefone: this.dadosEmpresa.telefone,
          celular: this.dadosEmpresa.celular,
          site: this.dadosEmpresa.site,
          cep: this.dadosEmpresa.cep,
          bairro: this.dadosEmpresa.bairro,
          rua: this.dadosEmpresa.rua,
          numero: this.dadosEmpresa.numero,
          complemento: this.dadosEmpresa.complemento
        });
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao carregar dados da empresa.');
      }
    });
  }

  onSubmit(): void {
    if (this.formDadosEmpresa.valid) {
      const form = this.formDadosEmpresa.value;
      const cnpjNumerico = this.toText(form.cnpj).replace(/\D/g, '').substring(0, 16);
      const payload = {
        IdEmpresa: this.dadosEmpresa?.idEmpresa || 0,
        NomeFantasia: form.nomeFantasia,
        Cnpj: cnpjNumerico,
        Contato: form.contato,
        RegistroBombeiros: form.telefone,
        RegistroPrefeitura: form.celular,
        RegistroCro: form.site,
        EnderecoEmpresaCadastroDto: {
          IdEnderecoEmpresa: this.dadosEmpresa?.idEnderecoEmpresa || 0,
          IdPais: Number(this.dadosEmpresa?.idPais || 0),
          IdEstado: Number(this.dadosEmpresa?.idEstado || 0),
          IdCidade: Number(this.dadosEmpresa?.idCidade || 0),
          Cep: form.cep,
          Bairro: form.bairro,
          Rua: form.rua,
          Numero: form.numero,
          Complemento: form.complemento || ''
        }
      };

      this.loading = true;
      this.util.showLoading();

      const url = API + 'empresa/Clinica/Save';
      this.ajaxService.post(url, payload).subscribe({
        next: (response) => {
          if (response?.hasErrors) {
            this.loading = false;
            this.util.closeLoading();
            this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar os dados da empresa.');
            return;
          }

          this.loading = false;
          this.util.closeLoading();

          this.dadosEmpresa = {
            ...(this.dadosEmpresa as DadosEmpresa),
            ...form,
            dataAtualizacao: new Date()
          };
          this.editando = false;
          this.util.toasterShowSuccess('Sucesso', 'Dados da empresa atualizados com sucesso!');
        },
        error: () => {
          this.loading = false;
          this.util.closeLoading();
          this.util.toasterShowError('Erro', 'Não foi possível salvar os dados da empresa.');
        }
      });
    } else {
      this.marcarCamposInvalidos();
    }
  }

  cancelarEdicao(): void {
    if (!this.dadosEmpresa) return;
    this.formDadosEmpresa.patchValue({
      nomeFantasia: this.dadosEmpresa.nomeFantasia,
      cnpj: this.dadosEmpresa.cnpj,
      contato: this.dadosEmpresa.contato,
      telefone: this.dadosEmpresa.telefone,
      celular: this.dadosEmpresa.celular,
      site: this.dadosEmpresa.site,
      cep: this.dadosEmpresa.cep,
      bairro: this.dadosEmpresa.bairro,
      rua: this.dadosEmpresa.rua,
      numero: this.dadosEmpresa.numero,
      complemento: this.dadosEmpresa.complemento
    });
    this.editando = false;
  }

  iniciarEdicao(): void {
    this.editando = true;
  }

  marcarCamposInvalidos(): void {
    Object.keys(this.formDadosEmpresa.controls).forEach(key => {
      const control = this.formDadosEmpresa.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.formDadosEmpresa.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.formDadosEmpresa.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'Este campo é obrigatório.';
      }
      if (field.errors['minlength']) {
        const requiredLength = field.errors['minlength'].requiredLength;
        return `Mínimo de ${requiredLength} caracteres.`;
      }
      if (field.errors['pattern']) {
        switch (fieldName) {
          case 'cnpj':
            return 'CNPJ deve conter entre 14 e 16 dígitos.';
          case 'telefone':
            return 'Telefone deve estar no formato (XX) XXXX-XXXX.';
          case 'celular':
            return 'Celular deve estar no formato (XX) XXXXX-XXXX.';
          case 'cep':
            return 'CEP deve estar no formato XXXXX-XXX.';
          case 'site':
            return 'Site deve começar com http:// ou https://.';
          default:
            return 'Formato inválido.';
        }
      }
    }
    return '';
  }

  formatarCNPJ(event: any): void {
    const value = event.target.value.replace(/\D/g, '').substring(0, 16);
    event.target.value = value;
    this.formDadosEmpresa.get('cnpj')?.setValue(value, { emitEvent: false });
  }

  formatarTelefone(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/^(\d{2})(\d)/, '($1) $2');
      if (value.length > 9) {
        value = value.replace(/(\d{4,5})(\d)/, '$1-$2');
      }
      event.target.value = value;
    }
  }

  formatarCEP(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 8) {
      value = value.replace(/^(\d{5})(\d)/, '$1-$2');
      event.target.value = value;
    }
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  private toNumberOrNull(value: any): number | null {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
} 